import { cevir } from "@/lib/dil";

/**
 * D-073 (U-072): ekranda doldurulan söküm formunun (FM-0011, Sokum.tsx `SokumForm`) parça bölümünün saf mantığı. JSX yok, React
 * yüklemez; bileşenler (`components/sokum-parca.tsx`) yalnız çizer ve çağırır.
 *
 * Bekir'in kararları: 24.10.2026 D1-D7 ("kırmızı olsun. Henüz farkları yönetmekle ilgili bir geliştirme yapmadık zaten."), 25.10.2026
 * R1-R8 (MRB'den gelen ID parçada okunup "Same" ya da "Different" ile teyit edilir; sade ekran; "Tüm önerilerin ok. Kodlamaya başla").
 *
 * İlke: yapılacak her işaretin kaynağı sunucudur. Parça "okunacak" yalnız `okumaGerekli`, "yeni ID gerekli" yalnız `kimlikGerekli`
 * doğruysa; print-out öncesi liste sunucunun `eksikler`inden; kırmızı `kirmizi`den, "Different"in türü `fark`tan. Web eski ID'nin
 * metninden hiçbir durum türetmez (iş emri numarası, rakamsız işaret, MRB değeri sunucunun kuralıdır). Teyidin kimi ve zamanı sunucunun;
 * istemci yalnız hangi düğmeye basıldığını ve sayfanın çizdiği durumu (`gorulen`) gönderir. MRB kaydı hiçbir yoldan değişmez.
 */

/* ------------------------------------------------------- sunucu yanıtı ------------------------------------------------------- */

/** Eski ID'nin MRB'ye karşı denetimi (G-04; rozetin dili). */
export type MrbDurum = "eslesti" | "heat_duzeyi" | "farkli" | "mrb_varyant" | "numara_duzeyi" | "oem_kaydi_yok" | "icerik_yok" | "kaynak_yok" | "bos";
/** Okuma durumunun tek kaynağı (sunucu `sokumBirimi`): yok = elle yazıldı · bekliyor = MRB değeri okunmadı · ayni · farkli. */
export type SokumOkuma = "yok" | "bekliyor" | "ayni" | "farkli";
/** "Different" basılmış parçanın okunan değeri (R2): kırmızı yalnız `okunamadi` ve `mrb_disi`. */
export type SokumFark = "okunamadi" | "mrb_disi" | "sira" | "mrb_varyant" | "numara_duzeyi" | "heat_duzeyi" | "denetlenemez";
export interface SokumTeyit { kim: string; ts: string }

/**
 * Bir parça (FM-0011 satırının yuvası) GET'te: saklanan anahtarlar (`eski`, `yeni`; formda ayrıca `gelmedi`, `mrb`, `mrbSn`, `teyit`,
 * `farkli`) ve sunucunun hesapladıkları. Excel'den okunan eski raporun yuvası da aynı hesaplanan anahtarları taşır; onun görünümü
 * (`EskiRaporKaydi`) bunlara bakmaz.
 */
export interface SokumParca {
  eski: string;
  yeni: string;
  gelmedi?: boolean;
  mrb?: string;
  mrbSn?: string;
  teyit?: SokumTeyit;
  farkli?: boolean;
  durum: MrbDurum;
  aciklama: string | null;
  okuma: SokumOkuma;
  fark: SokumFark | null;
  kirmizi: boolean;
  /** yalnız elle yazılmış kırmızı parçada: bulunmadi (MRB'de yok) · ayni_hat (iki C&K ID'si aynı MRB hattında) */
  kirmiziNeden?: "bulunmadi" | "ayni_hat";
  okumaGerekli: boolean;
  kimlikGerekli: boolean;
  son: string | null;
  ayniKimlik: boolean;
  eskiKullanilir: boolean;
  isEmri: boolean;
  /** "Use 025-21-5": yalnız yeni ID gereken ve yeni ID'si boş parçada */
  oneri: string | null;
  uyusmazlik: "bekliyor" | "yazildi" | null;
  /** yalnız C&K Lines satırında: imzalı yüklemenin yazacağı hat */
  hatRolu?: "K" | "C" | null;
}

export interface SokumFormSatiri {
  sira: number;
  ad: string;
  qty: number;
  kim: string | null;
  tarih: string | null;
  durum: "geldi" | "damgasiz" | "yok" | null;
  idler: SokumParca[];
  not: string | null;
  bulgu: boolean;
  fotolar: { ad: string; dokumanId: string }[];
  /** max(1, qty) */
  beklenen: number;
  /** geldi satırında beklenen eksi gelmeyen; öteki satırda null */
  gelen: number | null;
}

export type SokumEksik =
  | { tur: "assembly_sn" }
  | { tur: "durum"; sira: number }
  | { tur: "adet"; sira: number }
  | { tur: "okuma"; sira: number; k: number }
  | { tur: "kimlik"; sira: number; k: number };

/** Kimlik uyuşmazlığı kaydı; `parca`: metni bugün kırmızı olan bir "Different" parçanınkiyle aynıysa o parça. */
export interface SokumUyusmazlikKaydi { id: string; girdi: string; aciklama: string | null; parca?: { sira: number; k: number } | null }

export interface SokumFormKaydi {
  rn: string;
  tur: string;
  assemblySn: string | null;
  asnDurum: { durum: MrbDurum; aciklama: string | null };
  mrbIcerikVar: boolean;
  printed: boolean;
  imzali: boolean;
  satirlar: SokumFormSatiri[];
  eksikler: SokumEksik[];
  uyusmazliklar: SokumUyusmazlikKaydi[];
  /** MRB doldurmasının şimdi yazacağı hücre sayısı (yazmadan hesaplanır) */
  mrbDolacak: number;
}

/* ------------------------------------------------------- parça ------------------------------------------------------- */

/**
 * Parçanın hâli (tasarım 3.2.5): A gelmedi · B MRB değeri okunmadı · C Same · D Different, MRB'de yok (kırmızı) · E Different, ID
 * okunamadı (kırmızı, yeni ID gerekir) · F Different, satırın başka MRB ID'si (gri) · G Different, parçanın MRB'de bilinen başka değeri
 * (sarı etiket) · G2 Different, denetlenemedi (gri) · H elle yazılmış kullanılabilir eski ID · I elle, kullanılabilir eski ID yok.
 */
export type ParcaHali = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "G2" | "H" | "I";

export function parcaHali(b: SokumParca): ParcaHali {
  if (b.gelmedi) return "A";
  if (b.okuma === "bekliyor") return "B";
  if (b.okuma === "ayni") return "C";
  if (b.okuma === "farkli") {
    switch (b.fark) {
      case "okunamadi": return "E";
      case "mrb_disi": return "D";
      case "sira": return "F";
      case "mrb_varyant": case "numara_duzeyi": case "heat_duzeyi": return "G";
      default: return "G2";
    }
  }
  return b.eskiKullanilir ? "H" : "I";
}

/** MRB'nin parçayı başka bir değer olarak bildiği hâllerin bugünkü etiketi (rozetle aynı metin; sarı). */
function digerDegerEtiketi(d: MrbDurum | SokumFark | null | undefined): string | null {
  if (d === "mrb_varyant") return cevir("IN MRB · OTHER VALUE");
  if (d === "numara_duzeyi") return cevir("IN MRB · NUMBER ONLY");
  if (d === "heat_duzeyi") return cevir("MRB heat level");
  return null;
}

export type MetinTonu = "ok" | "warn" | "bad" | "ink-3";
/** "Recorded as" hücresinin öğeleri: kâğıda geçen değer ve küçük notu. */
export type SonOge =
  | { tur: "kimlik"; metin: string; kirmizi?: boolean }
  | { tur: "kalin"; metin: string }
  | { tur: "not"; metin: string; ton: MetinTonu }
  | { tur: "yapilacak"; metin: string; ton: "warn" | "bad" }
  | { tur: "etiket"; metin: string; ton: "warn" | "bad" };

export interface ParcaModeli {
  k: number;
  /** ekrandaki numara (1'den) */
  no: number;
  hal: ParcaHali;
  birim: SokumParca;
  /** satırın zemini: taralı gri · kırmızı · sarı (yapılacak) · yok */
  zemin: "gelmedi" | "kirmizi" | "yapilacak" | null;
  eski: { tur: "tire" } | { tur: "mrb"; deger: string; ustuCizili: boolean } | { tur: "girdi"; deger: string };
  /** C&K yuvasının hattı ("Kill line" / "Choke line"); yoksa null */
  hat: string | null;
  oku:
    | { tur: "tire" }
    | { tur: "bekliyor" }
    | { tur: "ayni"; teyit: SokumTeyit | null }
    | { tur: "farkli"; okunan: string; teyit: SokumTeyit | null }
    | { tur: "not"; metin: string; ton: MetinTonu };
  yeni: { tur: "tire" } | { tur: "girdi"; deger: string; yerTutucu: string; gerekli: boolean; oneri: string | null };
  son: SonOge[];
  okumaGerekli: boolean;
  kimlikGerekli: boolean;
  kirmizi: boolean;
  /** parça tamam: gelmedi ya da okuma ve kimlik işi yok (kırmızı parça tamamdır, yeni ID gerekmiyorsa) */
  tamam: boolean;
  /** satırın altındaki MRB kanıtı (bugünkü bant; metin sunucunun, Türkçe kipte sözlükteki kalıplarla çevrilir) */
  kanit: { tip: "duvar" | "not"; metin: string } | null;
}

/** Bir parçanın ekranda ve kâğıtta görünen her şeyi. `cokParcali`: satırda birden çok parça var (kanıtın "#k · " öneki). */
export function parcaModeli(b: SokumParca, k: number, cokParcali: boolean): ParcaModeli {
  const hal = parcaHali(b);
  const yeni = b.yeni.trim();
  const mrb = b.mrb ?? "";
  const kimlikGerekli = !b.gelmedi && b.kimlikGerekli;
  const okumaGerekli = !b.gelmedi && b.okumaGerekli;
  const kirmizi = !b.gelmedi && b.kirmizi;
  const yeniSatiri: SonOge[] = [{ tur: "kimlik", metin: yeni }, { tur: "not", metin: cevir("new ID (hard-stamp)"), ton: "ink-3" }];

  let son: SonOge[];
  switch (hal) {
    case "A":
      son = [{ tur: "kalin", metin: cevir("Not arrived") }, { tur: "not", metin: cevir("no ID needed"), ton: "ink-3" }];
      break;
    case "B":
      son = [{ tur: "yapilacak", metin: cevir("Read the ID on the piece"), ton: "warn" }];
      break;
    case "C":
      son = yeni ? [...yeniSatiri] : [{ tur: "kimlik", metin: b.eski }, { tur: "not", metin: cevir("read, same as MRB"), ton: "ok" }];
      break;
    case "D": {
      const isaret: SonOge[] = [{ tur: "kimlik", metin: b.eski, kirmizi: true }, { tur: "not", metin: cevir("different from MRB ({0})", mrb), ton: "bad" }];
      son = yeni ? [...yeniSatiri, ...isaret] : isaret;
      break;
    }
    case "E":
      son = yeni
        ? [...yeniSatiri, { tur: "yapilacak", metin: cevir("No ID readable"), ton: "bad" }]
        : [{ tur: "yapilacak", metin: cevir("No ID readable"), ton: "bad" }];
      break;
    case "F": {
      const isaret: SonOge[] = [{ tur: "kimlik", metin: b.eski }, { tur: "not", metin: cevir("Order differs from the MRB list"), ton: "ink-3" }];
      son = yeni ? [...yeniSatiri, ...isaret] : isaret;
      break;
    }
    case "G": {
      const isaret: SonOge[] = [{ tur: "kimlik", metin: b.eski }, { tur: "etiket", metin: digerDegerEtiketi(b.fark) ?? "", ton: "warn" }];
      son = yeni ? [...yeniSatiri, ...isaret] : isaret;
      break;
    }
    case "G2": {
      const isaret: SonOge[] = [{ tur: "kimlik", metin: b.eski }, { tur: "not", metin: cevir("different from MRB ({0})", mrb), ton: "ink-3" }];
      son = yeni ? [...yeniSatiri, ...isaret] : isaret;
      break;
    }
    case "H":
    case "I": {
      // Elle yazılmış eski ID kırmızıysa (MRB'de yok, ya da iki C&K ID'si aynı MRB hattında) değer kırmızı, notu ona göre; MRB'nin
      // başka değer olarak bildiği ID sarı etiketini taşır. Kullanılabilir eski ID yoksa (I) kâğıda yeni ID geçer.
      const kirmiziIsaret: SonOge[] = !kirmizi || !b.eski.trim() ? []
        : [{ tur: "kimlik", metin: b.eski, kirmizi: true }, b.kirmiziNeden === "ayni_hat" ? { tur: "etiket", metin: cevir("DIFFERENT IN MRB"), ton: "bad" } : { tur: "not", metin: cevir("not in the MRB"), ton: "bad" }];
      const sari = digerDegerEtiketi(b.durum);
      const sariIsaret: SonOge[] = !kirmizi && sari ? [{ tur: "etiket", metin: sari, ton: "warn" }] : [];
      const temel: SonOge[] = yeni ? [...yeniSatiri] : hal === "H" && !kirmiziIsaret.length ? [{ tur: "kimlik", metin: b.eski }, { tur: "not", metin: cevir("same ID"), ton: "ink-3" }] : [];
      son = [...temel, ...kirmiziIsaret, ...sariIsaret];
      break;
    }
  }
  // Yeni ID gerekliyse (yalnız sunucunun bayrağı) kâğıda geçen hücre bunu söyler.
  if (kimlikGerekli) son.push({ tur: "yapilacak", metin: cevir("New ID needed"), ton: "warn" });

  const eski: ParcaModeli["eski"] = hal === "A" ? { tur: "tire" }
    : hal === "B" || hal === "C" ? { tur: "mrb", deger: mrb || b.eski, ustuCizili: false }
    : hal === "H" || hal === "I" ? { tur: "girdi", deger: b.eski }
    : { tur: "mrb", deger: mrb, ustuCizili: true };
  const oku: ParcaModeli["oku"] = hal === "A" ? { tur: "tire" }
    : hal === "B" ? { tur: "bekliyor" }
    : hal === "C" ? { tur: "ayni", teyit: b.teyit ?? null }
    : hal === "H" ? { tur: "not", metin: cevir("✓ typed from the piece"), ton: "ok" }
    : hal === "I" ? (b.isEmri ? { tur: "not", metin: cevir("work order number, not an ID → new ID needed"), ton: "warn" } : { tur: "not", metin: cevir("no ID → new ID needed"), ton: "ink-3" })
    : { tur: "farkli", okunan: b.eski, teyit: b.teyit ?? null };
  // Kullanılabilir eski kimliği olmayan parçada yer tutucu "new ID", ötekilerde "keep same ID" (boş = aynı ID).
  const yeniGerekebilir = kimlikGerekli || hal === "E" || hal === "I";
  const yeniHucre: ParcaModeli["yeni"] = hal === "A" ? { tur: "tire" }
    : { tur: "girdi", deger: b.yeni, yerTutucu: yeniGerekebilir ? cevir("new ID") : cevir("keep same ID"), gerekli: kimlikGerekli, oneri: kimlikGerekli && !yeni ? b.oneri : null };

  const kanitli = (hal === "D" || hal === "G" || hal === "G2" || hal === "H" || hal === "I")
    && b.aciklama && (b.durum === "farkli" || b.durum === "mrb_varyant" || b.durum === "numara_duzeyi" || b.durum === "heat_duzeyi");
  // Kanıt cümlesi sunucunun İngilizce cümlesidir (sokum-mrb.service denetleRef); Türkçe kipte tr-elle.tsv'deki kalıplarla çevrilir
  // (denemeler turunun bulgusu: "Not found in the MRB. This part's MRB identity: …" Türkçe kipte İngilizce kalıyordu).
  const kanit = kanitli ? { tip: b.durum === "farkli" ? ("duvar" as const) : ("not" as const), metin: `${cokParcali ? `#${k + 1} · ` : ""}${cevir(b.aciklama!)}` } : null;
  // Hat adı çevrilmez (Kill, Choke); yalnız "line" sözcüğü. "Kill line" düz anahtar olsaydı sunucu cümlelerinde yakalanan hat adını da
  // çevirirdi (Continuity olaylarında hat adları İngilizce kalır).
  const hat = b.gelmedi || !b.hatRolu ? null : cevir("{0} line", b.hatRolu === "K" ? "Kill" : "Choke");

  return {
    k, no: k + 1, hal, birim: b,
    zemin: b.gelmedi ? "gelmedi" : kirmizi ? "kirmizi" : okumaGerekli || kimlikGerekli ? "yapilacak" : null,
    eski, hat, oku, yeni: yeniHucre, son,
    okumaGerekli, kimlikGerekli, kirmizi,
    tamam: Boolean(b.gelmedi) || (!okumaGerekli && !kimlikGerekli),
    kanit,
  };
}

/* ------------------------------------------------------- satır ------------------------------------------------------- */

export type HapTonu = "ok" | "warn" | "bad";
export interface SatirModeli {
  sira: number;
  /** FM-0011 satır numarası (1'den) */
  no: number;
  ad: string;
  satir: SokumFormSatiri;
  durum: "geldi" | "yok" | null;
  beklenen: number;
  parcalar: ParcaModeli[];
  haplar: { metin: string; ton: HapTonu }[];
  /** satır bitti: Not in assembly, ya da geldi ve hiçbir parçasında iş yok */
  tamam: boolean;
  /** kartın çerçevesi: kırmızı parça · yapılacak · düz */
  cerceve: "bad" | "warn" | "line";
  /** katlı satırın özeti ("6 arrived · 6 new IDs", "1 arrived · 1300520-5 read, same as MRB") */
  ozet: string;
  sayac: { toplam: number; tamam: number; kirmizi: number; gelmedi: number; okunacak: number; yeniGerekli: number };
}

const satirDurumu = (d: SokumFormSatiri["durum"]): SatirModeli["durum"] => (d === "geldi" || d === "yok" ? d : null);

export function satirModeli(x: SokumFormSatiri, adetEksik: boolean): SatirModeli {
  const durum = satirDurumu(x.durum);
  const beklenen = x.beklenen ?? Math.max(1, x.qty);
  const parcalar = durum === "geldi" ? x.idler.slice(0, beklenen).map((b, k) => parcaModeli(b, k, beklenen > 1)) : [];
  const say = (f: (p: ParcaModeli) => boolean) => parcalar.filter(f).length;
  const okunacak = say((p) => p.okumaGerekli);
  const yeniGerekli = say((p) => p.kimlikGerekli);
  const kirmizi = say((p) => p.kirmizi);
  const gelmedi = say((p) => p.hal === "A");
  const gelen = beklenen - gelmedi;

  const tamam = durum === "yok" || (durum === "geldi" && !okunacak && !yeniGerekli && !adetEksik && parcalar.length === beklenen);
  const haplar: SatirModeli["haplar"] = [];
  if (durum === "yok") haplar.push({ metin: cevir("✓ Done"), ton: "ok" });
  else if (durum === null) haplar.push({ metin: cevir("Status not chosen"), ton: "warn" });
  else {
    if (okunacak) haplar.push({ metin: cevir("{0} to check", okunacak), ton: "warn" });
    if (yeniGerekli) haplar.push({ metin: yeniGerekli === 1 ? cevir("{0} new ID needed", 1) : cevir("{0} new IDs needed", yeniGerekli), ton: "warn" });
    if (tamam) haplar.push({ metin: gelmedi ? cevir("✓ {0} arrived · {1} not arrived", gelen, gelmedi) : cevir("✓ Done"), ton: "ok" });
    if (kirmizi) haplar.push({ metin: cevir("{0} different", kirmizi), ton: "bad" });
  }

  let ozet: string;
  if (durum === "yok") ozet = cevir("not in assembly (None)");
  else if (durum === null) ozet = cevir("Status not chosen");
  else if (beklenen === 1 && parcalar[0]) {
    const p = parcalar[0];
    const yeni = p.birim.yeni.trim();
    const deger = yeni ? cevir("new ID {0}", yeni) : p.son.map((o) => o.metin).filter(Boolean).join(" ");
    ozet = [cevir("{0} arrived", 1), deger].filter(Boolean).join(" · ");
  } else {
    const yeniler = say((p) => p.hal !== "A" && p.birim.yeni.trim() !== "");
    const ayni = say((p) => p.hal !== "A" && p.birim.ayniKimlik);
    ozet = [
      cevir("{0} arrived", gelen),
      gelmedi ? cevir("{0} not arrived", gelmedi) : "",
      yeniler === 1 ? cevir("1 new ID") : yeniler > 1 ? cevir("{0} new IDs", yeniler) : "",
      ayni ? cevir("{0} same ID", ayni) : "",
    ].filter(Boolean).join(" · ");
  }

  const yapilacak = !tamam;
  return {
    sira: x.sira, no: x.sira + 1, ad: x.ad, satir: x, durum, beklenen, parcalar, haplar, tamam,
    cerceve: kirmizi ? "bad" : yapilacak ? "warn" : "line",
    ozet,
    sayac: {
      toplam: beklenen,
      tamam: durum === "yok" ? beklenen : durum === "geldi" ? say((p) => p.tamam) : 0,
      kirmizi, gelmedi, okunacak, yeniGerekli,
    },
  };
}

/* ------------------------------------------------------- kayıt ------------------------------------------------------- */

export interface KontrolMaddesi {
  isaret: "ok" | "eksik";
  metin: string;
  /** "Go to row k →" (sira) ya da "Go to the header →" */
  git: { tur: "satir"; sira: number } | { tur: "baslik" } | null;
  /** Assembly SN girildi ama eşleşmedi: yanında bugünkü rozet */
  rozet?: boolean;
}
export interface UyusmazlikSatiri {
  sira: number;
  k: number;
  parca: string;
  /** MRB'nin verdiği ID; elle yazılmış parçada null ("—") */
  mrb: string | null;
  /** okunan ya da yazılan değer; okunamadıysa ("No ID readable") okunamadi true */
  okunan: string;
  okunamadi: boolean;
  not: string | null;
}
export interface FormModeli {
  satirlar: SatirModeli[];
  baslik: string;
  sayac: { toplam: number; tamam: number; yapilacak: number; kirmizi: number; gelmedi: number };
  /** süzgeç etiketlerinin sayıları: yapılacak satır, kırmızı parça */
  suzgecSayisi: { yapilacak: number; farkli: number };
  kontrol: KontrolMaddesi[];
  /** düğmenin yanındaki satır ("Opens when the 4 items above are done."); print-out açıksa null */
  printNotu: string | null;
  printAcik: boolean;
  uyusmazlik: { baslik: string | null; satirlar: UyusmazlikSatiri[]; onceki: string | null };
}

/** "Locking Inserts #5, #6 · C&K Lines #2": aynı satırın parçaları ", ", satırlar " · " ile. */
export function parcaListesi(l: readonly { ad: string; k: number }[]): string {
  const gruplar: { ad: string; no: string[] }[] = [];
  for (const p of l) {
    const son = gruplar.at(-1);
    if (son && son.ad === p.ad) son.no.push(`#${p.k + 1}`);
    else gruplar.push({ ad: p.ad, no: [`#${p.k + 1}`] });
  }
  return gruplar.map((g) => `${g.ad} ${g.no.join(", ")}`).join(" · ");
}

/** Print-out öncesi liste (sunucunun kapı maddelerinden) ve düğmenin satırı. Sayım `eksikler.length`: ret cümlesiyle aynı sayı. */
function kontrolListesi(kayit: SokumFormKaydi): { kontrol: KontrolMaddesi[]; printNotu: string | null } {
  const ad = (sira: number) => kayit.satirlar.find((x) => x.sira === sira)?.ad ?? `#${sira + 1}`;
  const e = kayit.eksikler;
  const kontrol: KontrolMaddesi[] = [];
  const sn = (kayit.assemblySn ?? "").trim();
  if (e.some((x) => x.tur === "assembly_sn") || !sn) kontrol.push({ isaret: "eksik", metin: cevir("Assembly SN not entered yet"), git: { tur: "baslik" } });
  else if (kayit.asnDurum.durum === "eslesti") kontrol.push({ isaret: "ok", metin: cevir("Assembly SN {0} matches the MRB", sn), git: null });
  else kontrol.push({ isaret: "ok", metin: cevir("Assembly SN {0} entered", sn), git: null, rozet: true });

  const durumlar = e.filter((x): x is Extract<SokumEksik, { tur: "durum" }> => x.tur === "durum");
  if (durumlar.length) {
    const adlar = durumlar.map((x) => ad(x.sira)).join(" · ");
    kontrol.push({ isaret: "eksik", metin: durumlar.length === 1 ? cevir("1 row has no status yet: {0}", adlar) : cevir("{0} rows have no status yet: {1}", durumlar.length, adlar), git: { tur: "satir", sira: durumlar[0]!.sira } });
  } else kontrol.push({ isaret: "ok", metin: cevir("A status is chosen on all {0} rows", kayit.satirlar.length), git: null });

  const okumalar = e.filter((x): x is Extract<SokumEksik, { tur: "okuma" }> => x.tur === "okuma");
  if (okumalar.length) {
    const liste = parcaListesi(okumalar.map((x) => ({ ad: ad(x.sira), k: x.k })));
    kontrol.push({ isaret: "eksik", metin: okumalar.length === 1 ? cevir("1 MRB ID not read yet: {0}", liste) : cevir("{0} MRB IDs not read yet: {1}", okumalar.length, liste), git: { tur: "satir", sira: okumalar[0]!.sira } });
  }
  const kimlikler = e.filter((x): x is Extract<SokumEksik, { tur: "kimlik" }> => x.tur === "kimlik");
  if (kimlikler.length) {
    const liste = parcaListesi(kimlikler.map((x) => ({ ad: ad(x.sira), k: x.k })));
    kontrol.push({ isaret: "eksik", metin: kimlikler.length === 1 ? cevir("1 piece needs a new ID: {0}", liste) : cevir("{0} pieces need a new ID: {1}", kimlikler.length, liste), git: { tur: "satir", sira: kimlikler[0]!.sira } });
  }
  for (const x of e) {
    if (x.tur === "adet") kontrol.push({ isaret: "eksik", metin: cevir("Row {0}: the pieces do not match the quantity; reload the form.", x.sira + 1), git: { tur: "satir", sira: x.sira } });
  }
  const n = e.length;
  return { kontrol, printNotu: n === 0 ? null : n === 1 ? cevir("Opens when the item above is done.") : cevir("Opens when the {0} items above are done.", n) };
}

/** Discrepancy tablosu: bugün kırmızı her parça bir satır; altında bugünkü bir parçaya ait olmayan önceki kayıtlar. */
function uyusmazlikTablosu(kayit: SokumFormKaydi, satirlar: readonly SatirModeli[]): FormModeli["uyusmazlik"] {
  const l: UyusmazlikSatiri[] = [];
  for (const s of satirlar) {
    for (const p of s.parcalar) {
      if (!p.kirmizi) continue;
      const b = p.birim;
      const yazilmis = b.okuma === "yok";
      const not = yazilmis
        ? b.kirmiziNeden === "ayni_hat" ? (b.aciklama ? cevir(b.aciklama) : b.aciklama) : cevir("Typed from the piece; not in the MRB (evidence under row {0}). MRB is not changed.", s.no)
        : b.uyusmazlik === "yazildi" ? cevir("Printed in red. Written to the record. MRB is not changed.")
        : b.uyusmazlik === "bekliyor" ? (kayit.printed ? cevir("(written when the signed print-out is uploaded)") : cevir("Printed in red. Written to the record when the print-out is generated. MRB is not changed."))
        : null;
      l.push({ sira: s.sira, k: p.k, parca: `${s.ad} #${p.no}`, mrb: yazilmis ? null : b.mrb ?? null, okunan: b.eski.trim(), okunamadi: b.fark === "okunamadi", not });
    }
  }
  const onceki = kayit.uyusmazliklar.filter((u) => !u.parca);
  return {
    baslik: !l.length ? null : l.length === 1 ? cevir("Discrepancy record · {0} piece different from MRB", 1) : cevir("Discrepancy record · {0} pieces different from MRB", l.length),
    satirlar: l,
    onceki: onceki.length ? onceki.map((u) => `${cevir(u.aciklama ?? "")}${u.girdi ? ` ('${u.girdi}')` : ""}`).join(" · ") : null,
  };
}

/** Formun parça bölümünün, print-out öncesi listesinin ve Discrepancy tablosunun modeli. */
export function sokumFormModeli(kayit: SokumFormKaydi): FormModeli {
  const adetEksik = new Set(kayit.eksikler.filter((e) => e.tur === "adet").map((e) => (e as { sira: number }).sira));
  const satirlar = [...kayit.satirlar].sort((a, b) => a.sira - b.sira).map((x) => satirModeli(x, adetEksik.has(x.sira)));
  const toplam = satirlar.reduce((t, s) => t + s.beklenen, 0);
  const tamam = satirlar.reduce((t, s) => t + s.sayac.tamam, 0);
  const kirmizi = satirlar.reduce((t, s) => t + s.sayac.kirmizi, 0);
  const gelmedi = satirlar.reduce((t, s) => t + s.sayac.gelmedi, 0);
  const { kontrol, printNotu } = kontrolListesi(kayit);
  return {
    satirlar,
    baslik: cevir("Parts recorded during disassembly — {0} rows, {1} pieces", satirlar.length, toplam),
    sayac: { toplam, tamam, yapilacak: toplam - tamam, kirmizi, gelmedi },
    suzgecSayisi: { yapilacak: satirlar.filter((s) => !s.tamam).length, farkli: kirmizi },
    kontrol,
    printNotu,
    printAcik: kayit.eksikler.length === 0,
    uyusmazlik: uyusmazlikTablosu(kayit, satirlar),
  };
}

/* ------------------------------------------------------- katlama ve süzgeç ------------------------------------------------------- */

export type Suzgec = "hepsi" | "yapilacak" | "farkli";
/**
 * Görünüm durumu (yalnız ekranda, saklanmaz). `acik` ilk yüklemede bitmemiş satırlardır; sonra yalnız kişinin "Open", "Fold" ve
 * "Go to row k →" düğmeleriyle değişir: kayıt yanıtı hiçbir satırı açmaz ya da katlamaz (R8 "no jumping"). Süzgecin gösterdiği satırlar
 * süzgeç seçildiği anda alınır; süzgece artık uymayan satır kişi süzgeci yeniden seçene kadar görünür kalır (JC2-7).
 */
export interface GorunumDurumu { acik: ReadonlySet<number>; suzgec: Suzgec; suzgecSatirlari: ReadonlySet<number> | null }

export function ilkGorunum(m: FormModeli): GorunumDurumu {
  return { acik: new Set(m.satirlar.filter((s) => !s.tamam).map((s) => s.sira)), suzgec: "hepsi", suzgecSatirlari: null };
}

const suzgeceUyar = (s: SatirModeli, f: Suzgec) => (f === "hepsi" ? true : f === "yapilacak" ? !s.tamam : s.sayac.kirmizi > 0);

export function suzgecSec(d: GorunumDurumu, m: FormModeli, f: Suzgec): GorunumDurumu {
  return { ...d, suzgec: f, suzgecSatirlari: f === "hepsi" ? null : new Set(m.satirlar.filter((s) => suzgeceUyar(s, f)).map((s) => s.sira)) };
}
export function satirAc(d: GorunumDurumu, sira: number): GorunumDurumu {
  return d.acik.has(sira) ? d : { ...d, acik: new Set([...d.acik, sira]) };
}
export function satirKatla(d: GorunumDurumu, sira: number): GorunumDurumu {
  if (!d.acik.has(sira)) return d;
  const acik = new Set(d.acik);
  acik.delete(sira);
  return { ...d, acik };
}
/** "Go to row k →": satırı açar; süzgeç onu gizliyorsa süzgeç kalkar. */
export function satiraGit(d: GorunumDurumu, sira: number): GorunumDurumu {
  const acilmis = satirAc(d, sira);
  return satirGorunurMu(acilmis, sira) ? acilmis : { ...acilmis, suzgec: "hepsi", suzgecSatirlari: null };
}
export const satirGorunurMu = (d: GorunumDurumu, sira: number) => d.suzgecSatirlari === null || d.suzgecSatirlari.has(sira);
export const satirAcikMi = (d: GorunumDurumu, sira: number) => d.acik.has(sira);

/** Süzgecin üç etiketi. */
export function suzgecEtiketleri(m: FormModeli): Record<Suzgec, string> {
  const n = m.suzgecSayisi.yapilacak;
  return { hepsi: cevir("All"), yapilacak: n === 1 ? cevir("To do (1 row)") : cevir("To do ({0} rows)", n), farkli: cevir("Different ({0})", m.suzgecSayisi.farkli) };
}

/* ------------------------------------------------------- gövdeler ------------------------------------------------------- */

/** Parça gövdesi (`PATCH /sokum/:id`, `satirlar[sira].birimler[k]`). `gorulen`: sayfanın çizdiği MRB değeri ve teyit zamanı (K41). */
export interface ParcaGovdesi { eski?: string; yeni?: string; gelmedi?: boolean; teyit?: "ayni" | "farkli" | null; gorulen?: { mrb: string; ts: string | null } }

/** MRB değerli yuvanın okuma durumuna dokunan her gövde sayfanın çizdiği durumu taşır; sunucu uyuşmazsa 409 ile reddeder. */
export function parcaGovdesi(b: Pick<SokumParca, "mrb" | "teyit">, alanlar: Omit<ParcaGovdesi, "gorulen">): ParcaGovdesi {
  const okumaya = alanlar.teyit !== undefined || alanlar.eski !== undefined || alanlar.gelmedi !== undefined;
  return b.mrb && okumaya ? { ...alanlar, gorulen: { mrb: b.mrb, ts: b.teyit?.ts ?? null } } : { ...alanlar };
}
export const ayniGovdesi = (b: Pick<SokumParca, "mrb" | "teyit">) => parcaGovdesi(b, { teyit: "ayni" });
/** "Different": okunan alanından çıkınca tek istekte (JC2-3); boş alan = ID okunamadı. */
export const farkliGovdesi = (b: Pick<SokumParca, "mrb" | "teyit">, okunan: string) => parcaGovdesi(b, { teyit: "farkli", eski: okunan });
/** Kaydedilmiş "Different"in okunan değeri düzeltildi. */
export const okunanGovdesi = (b: Pick<SokumParca, "mrb" | "teyit">, okunan: string) => parcaGovdesi(b, { eski: okunan });
export const geriAlGovdesi = (b: Pick<SokumParca, "mrb" | "teyit">) => parcaGovdesi(b, { teyit: null });
export const gelmediGovdesi = (b: Pick<SokumParca, "mrb" | "teyit">, gelmedi: boolean) => parcaGovdesi(b, { gelmedi });
export const eskiGovdesi = (b: Pick<SokumParca, "mrb" | "teyit">, eski: string) => parcaGovdesi(b, { eski });
export const yeniGovdesi = (yeni: string): ParcaGovdesi => ({ yeni });
/** PATCH gövdesi: tek satırın tek parçası. */
export const satirParcaGovdesi = (sira: number, k: number, govde: ParcaGovdesi) => ({ satirlar: { [sira]: { birimler: { [k]: govde } } } });

/** Parça satırının gönderdiği alanlar; `gorulen` gönderilirken eklenir. */
export type ParcaAlanlari = Omit<ParcaGovdesi, "gorulen">;
/**
 * İnceleme turu (25.10.2026): parça gövdesi GÖNDERİLDİĞİ anda son kaydedilmiş kayıttan kurulur (`gorulen` o anki MRB değeri ve teyit
 * zamanı). Aynı parçaya art arda iki kayıt (okunan değeri düzeltip hemen "Undo") ilkinin yeni damgasını görür; tıklama anındaki eski
 * damgayla gitseydi kişinin kendi kaydı yüzünden 409 alırdı.
 */
export function guncelParcaGovdesi(kayit: Pick<SokumFormKaydi, "satirlar"> | null, sira: number, k: number, alanlar: ParcaAlanlari) {
  const b = kayit?.satirlar.find((x) => x.sira === sira)?.idler[k];
  return satirParcaGovdesi(sira, k, b ? parcaGovdesi(b, alanlar) : { ...alanlar });
}

/** İnceleme turu: formun kayıtları sırayla gider; bir kayıt öncekinin cevabı gelmeden başlamaz (reddedilen kayıt sırayı durdurmaz). */
export function siraliKuyruk() {
  let son: Promise<unknown> = Promise.resolve();
  return <T>(is: () => Promise<T>): Promise<T> => {
    const p = son.then(is);
    son = p.catch(() => undefined);
    return p;
  };
}

/** Adet kutusundan çıkış: boş, tam sayı olmayan ya da eksi değer ve aynı adet kayıt değildir (null); boş kutu adedi 0 yapmaz. */
export function adetGirdisi(metin: string, mevcut: number): number | null {
  const s = metin.trim();
  if (!/^\d{1,4}$/.test(s)) return null;
  const n = Number(s);
  return n === mevcut ? null : n;
}

/**
 * "New ID" kutusundan çıkış: gönderilecek yeni ID, gönderilecek bir şey yoksa null. İkinci inceleme bulgusu (25.10.2026): kişi kutuda
 * yazdıysa (`yazildi`: silip aynı metni yeniden yazmak dahil) değer kayıtlıyla aynı kalsa da gönderilir; sunucu D-073 göçünün görünmeyen
 * `otoYeni` işaretini yalnız kişinin yeni ID yazan gövdesiyle kaldırır (önceki kodun önerisi kişinin değeri olur, doldurmada boşalmaz).
 * İşaretsiz yuvada aynı değer sunucuda hiçbir şey yazmaz. Kutuya yazılmadıysa ve değer aynıysa hiçbir şey gönderilmez (bugünkü gibi).
 */
export function yeniIdCikisi(deger: string, kayitli: string, yazildi: boolean): string | null {
  return deger !== kayitli || yazildi ? deger.trim() : null;
}

/** "Not in assembly"a geçmeden sorulur mu: satırda kişinin yazdığı, okuduğu ya da verdiği bir ID varsa (K36, T21). */
export function girilmisIdVar(x: Pick<SokumFormSatiri, "durum" | "idler">): boolean {
  return x.durum === "geldi" && x.idler.some((b) => !b.gelmedi && ((!b.mrb && b.eski.trim() !== "") || Boolean(b.teyit) || b.yeni.trim() !== ""));
}

/**
 * JC2-26: açık, print-out'u üretilmemiş, eşleşmiş formun MRB'nin şimdi yazacağı boş hücreleri varsa sayfa yüklenince bir kez boş kayıt
 * gönderilir (sunucunun doldurması çalışır). Yalnız kilitsiz formda ve `sokum` izni olan kişide; yanıttan sonra bir daha gönderilmez
 * (çağıran bir kez sorar).
 */
export function acilistaDoldurulur(kayit: Pick<SokumFormKaydi, "tur" | "imzali" | "mrbDolacak">, yetkili: boolean): boolean {
  return kayit.tur === "form" && !kayit.imzali && kayit.mrbDolacak > 0 && yetkili;
}
